import {
    Colors,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import { QuestionTextRenderer } from '../QuestionTextRenderer/QuestionTextRenderer';

const MIN_WIDTH_PER_POINT = 70;
const LIKERT_GRADIENT = `linear-gradient(to right, ${Colors.CHART_RED}, ${Colors.CHART_ORANGE}, ${Colors.CHART_YELLOW}, ${Colors.CHART_LIME}, ${Colors.CHART_GREEN})`;

interface ScaleBarData {
    actualPercentage: number;
    normalizedHeight: number;
    emptyHeight: number;
}

export interface LikertScaleProps {
    /** Data object where keys are scale values and values are percentages (0-100) */
    data: Record<number, number>;
    /** Minimum scale value */
    minValue: number;
    /** Maximum scale value */
    maxValue: number;
    /** Label for minimum value */
    startLabel?: string;
    /** Label for maximum value */
    endLabel?: string;
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

const LikertScale = ({
    data,
    minValue,
    maxValue,
    startLabel,
    endLabel,
    className = '',
    dataTestId = 'likert-scale',
}: LikertScaleProps) => {
    const scaleValues: number[] = [];
    for (let i = minValue; i <= maxValue; i++) {
        scaleValues.push(i);
    }

    const percentages = Object.values(data);
    const maxPercentage = percentages.length > 0 ? Math.max(...percentages) : 100;

    const getBarHeight = (value: number): number => {
        const percentage = data[value] || 0;
        if (maxPercentage === 0) return 0;
        return (percentage / maxPercentage) * 100;
    };

    const formatValue = (value: number): string => value.toString().padStart(2, '0');

    const containerMinWidth = scaleValues.length * MIN_WIDTH_PER_POINT;

    const getScaleBarData = (value: number): ScaleBarData => {
        const actualPercentage = data[value] || 0;
        const normalizedHeight = getBarHeight(value);
        return {
            actualPercentage,
            normalizedHeight,
            emptyHeight: 100 - normalizedHeight,
        };
    };

    return (
        <div className={`w-full ${className}`}>
            <div
                className={'scrollbar-glass max-w-full overflow-auto pb-2.5'}
                data-testid={dataTestId}
            >
                <div style={{ minWidth: `${containerMinWidth}px` }}>
                    <div
                        className={'relative flex h-[100px] items-end overflow-hidden rounded-md'}
                        style={{ background: LIKERT_GRADIENT }}
                    >
                        <TooltipProvider>
                            {scaleValues.map((value) => {
                                const { actualPercentage, emptyHeight } = getScaleBarData(value);

                                return (
                                    <Tooltip
                                        key={value}
                                        delayDuration={200}
                                    >
                                        <TooltipTrigger asChild>
                                            <div
                                                className={
                                                    'relative flex h-full flex-1 cursor-default border-r border-white last:border-r-0'
                                                }
                                                data-testid={`${dataTestId}-bar-${value}`}
                                            >
                                                <div
                                                    className={
                                                        'absolute inset-0 flex h-full flex-col'
                                                    }
                                                >
                                                    <div
                                                        className={'w-full bg-neutral-100'}
                                                        style={{ height: `${emptyHeight}%` }}
                                                    />

                                                    <div className={'flex-1'} />
                                                </div>

                                                <div
                                                    className={
                                                        'absolute inset-0 flex items-center justify-center'
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            'flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm'
                                                        }
                                                        data-testid={`${dataTestId}-circle-${value}`}
                                                    >
                                                        <Text
                                                            size={'small'}
                                                            weight={'semibold'}
                                                            className={'text-xs'}
                                                        >
                                                            {formatValue(value)}
                                                        </Text>
                                                    </div>
                                                </div>

                                                <div
                                                    className={
                                                        'absolute right-0 bottom-0 left-0 flex justify-center pb-2'
                                                    }
                                                >
                                                    <div
                                                        data-testid={`${dataTestId}-percentage-${value}`}
                                                    >
                                                        <Text
                                                            size={'small'}
                                                            weight={'medium'}
                                                            className={'text-xs'}
                                                        >
                                                            ({actualPercentage}%)
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </TooltipTrigger>

                                        <TooltipContent
                                            className={'pointer-events-auto'}
                                            side={'top'}
                                        >
                                            {`${actualPercentage}%`}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            {(startLabel || endLabel) && (
                <div className={'mt-4 flex justify-between px-2'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'max-w-[40%]'}
                    >
                        {startLabel && <QuestionTextRenderer text={startLabel} />}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'max-w-[40%] text-right'}
                    >
                        {endLabel && <QuestionTextRenderer text={endLabel} />}
                    </Text>
                </div>
            )}
        </div>
    );
};

export { LikertScale };
export default LikertScale;
